<script setup lang="ts">
/**
 * 错误码 → 界面的**唯一**接线点（契约 §5.2）。
 *
 * `api-client` 只广播事实（"额度用完了"、"被限流了"），不引任何 Vue 代码；
 * 由这个组件把它们翻译成弹窗 / 提示条 / 倒计时 / 登出。
 * 好处是 api-client 可以在 vitest 的 node 环境里裸跑，
 * 不必为了测一个 42901 而挂载整个应用。
 *
 * 细节：额度弹窗用 Modal 而不是 toast——它必须比提示条更醒目，
 * 而且要挡住"再试一次"的冲动；维护公告的 durationMs 是 0（永不自动消失），
 * 因为它描述的是一个持续状态，自动消失会让人以为已经恢复。
 *
 * 42900 的弹窗按 `scope` 分岔（details 带上了被拦的那一维）：
 * 日额度与月额度的恢复时间差了一个量级，文案只说"今日"会让月度被拦的用户
 * 白等一整天。主按钮给的是「升级套餐」——额度用完时用户真正能做的只有两件事：
 * 等，或者升档。
 *
 * v1.3.0：details 里已经没有绝对 token 量了，所以这里只说"剩多少百分比"与
 * "什么时候恢复"。百分比是**服务端算的**，弹窗不参与任何换算。
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import Modal from '../ui/Modal.vue';
import Button from '../ui/Button.vue';
import { pushToast } from '../ui/toast-store';
import { onApiEvent } from '../../lib/api-events';
import type { QuotaExceededDetails } from '../../lib/api-events';
import { markAnonymous } from '../../lib/auth-store';
import { formatDateTime } from '../../lib/format';
import { applyRateLimit } from '../../lib/rate-limit';

/** 额度弹窗的载荷：错误消息 + 被拦的那一维。 */
interface QuotaAlert extends QuotaExceededDetails {
  message: string;
}

const quotaAlert = ref<QuotaAlert | null>(null);

const quotaTitle = computed(() => (quotaAlert.value?.scope === 'monthly' ? '本月额度已用完' : '今日额度已用完'));

/**
 * 描述：说清"哪一维、还剩多少、什么时候恢复"。
 *
 * `resetAt` 为空串时（快照缺失、或旧的错误路径）退回一句不带时间的说明：
 * 显示 `Invalid Date` 比不显示时间更糟，用户会以为额度坏了。
 */
const quotaDescription = computed(() => {
  const alert = quotaAlert.value;
  if (alert === null) {
    return '';
  }
  const scopeText = alert.scope === 'monthly' ? '本月' : '今日';
  const parts: string[] = [alert.message.length > 0 ? alert.message : `${scopeText}额度已用完`];
  parts.push(`剩余额度 ${alert.remainingPercent}%`);
  parts.push(alert.resetAt.length > 0 ? `将于 ${formatDateTime(alert.resetAt)} 重置` : '将在重置时间后恢复');
  return `${parts.join('；')}。升级套餐可立即提高额度。`;
});

let unsubscribe: (() => void) | null = null;

onMounted(() => {
  unsubscribe = onApiEvent((event) => {
    switch (event.type) {
      case 'quota-exceeded':
        quotaAlert.value = {
          message: event.message,
          scope: event.scope,
          remainingPercent: event.remainingPercent,
          resetAt: event.resetAt,
        };
        break;
      case 'rate-limited':
        applyRateLimit(event.retryAfterSeconds);
        pushToast({
          tone: 'warning',
          title: '请求过于频繁',
          description: `${event.retryAfterSeconds} 秒后可再次发送。`,
          durationMs: 4000,
        });
        break;
      case 'unauthorized':
        markAnonymous(event.message);
        pushToast({
          tone: 'danger',
          title: '登录状态已失效',
          description: '请重新登录后继续，已输入的内容不会丢失。',
        });
        break;
      case 'upstream-failure':
        pushToast({
          tone: 'warning',
          title: '本次回答未能完成',
          description: event.notBillable ? `${event.message}（本次未消耗额度）` : event.message,
        });
        break;
      case 'maintenance':
        pushToast({
          tone: 'warning',
          title: '服务维护中',
          description: event.message,
          durationMs: 0,
        });
        break;
    }
  });
});

onBeforeUnmount(() => {
  unsubscribe?.();
  unsubscribe = null;
});
</script>

<template>
  <Modal
    :open="quotaAlert !== null"
    :title="quotaTitle"
    :description="quotaDescription"
    @close="quotaAlert = null"
  >
    <template #footer>
      <Button variant="ghost" size="sm" @click="quotaAlert = null">知道了</Button>
      <RouterLink to="/checkout" @click="quotaAlert = null">
        <Button size="sm">升级套餐</Button>
      </RouterLink>
    </template>
  </Modal>
</template>
